import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// Constants
import { LOCAL_STORAGE_KEYS } from '@constants/storage.constants';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@constants/language.constants';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly translate = inject(TranslateService);

  private readonly defaultLanguage = DEFAULT_LANGUAGE;
  private readonly supportedLanguages = SUPPORTED_LANGUAGES;

  initLanguage(): void {
    this.translate.addLangs([...this.supportedLanguages]);
    this.translate.setDefaultLang(this.defaultLanguage);

    const storedLanguage = this.getLanguage(LOCAL_STORAGE_KEYS.APP_LANG);
    const language = this.isSupported(storedLanguage) ? storedLanguage : this.defaultLanguage;

    this.useLanguage(language);
  }

  useLanguage(language: string): void {
    const safeLanguage = this.isSupported(language) ? language : this.defaultLanguage;

    this.translate.use(safeLanguage).subscribe({
      error: () => {
        if (safeLanguage !== this.defaultLanguage) {
          this.translate.use(this.defaultLanguage).subscribe();
          this.setLanguage(LOCAL_STORAGE_KEYS.APP_LANG, this.defaultLanguage);
        }
      },
    });

    this.setLanguage(LOCAL_STORAGE_KEYS.APP_LANG, safeLanguage);
  }

  private isSupported(language: string): language is (typeof this.supportedLanguages)[number] {
    return (this.supportedLanguages as readonly string[]).includes(language);
  }

  private getLanguage(key: string): string {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  }

  private setLanguage(key: string, value: string): void {
    localStorage.setItem(key, value);
  }
}
